import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Home, Phone, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UmrahQuoteDepositSuccessPage() {
  const [state, setState] = useState({ loading: true, paid: false, transaction: null, error: null });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setState({ loading: false, paid: false, transaction: null, error: 'Missing session_id' });
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await fetch(`${API_URL}/api/umrah/quotation/deposit/status/${sessionId}`);
        if (!res.ok) throw new Error(`Status check failed (${res.status})`);
        const data = await res.json();
        if (cancelled) return;
        const tx = data.transaction || {};
        if (tx.payment_status === 'PAID') {
          setState({ loading: false, paid: true, transaction: tx, error: null });
          return;
        }
        if (attempts < 10) {
          setTimeout(poll, 1500);
        } else {
          setState({ loading: false, paid: false, transaction: tx, error: null });
        }
      } catch (e) {
        if (!cancelled) setState({ loading: false, paid: false, transaction: null, error: e.message || 'Could not check status' });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  const tx = state.transaction || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white p-6 sm:p-8 text-center">
            {state.loading ? (
              <Loader2 className="h-12 w-12 mx-auto animate-spin" />
            ) : state.paid ? (
              <CheckCircle2 className="h-14 w-14 mx-auto" />
            ) : (
              <AlertTriangle className="h-12 w-12 mx-auto" />
            )}
            <h1 className="text-2xl sm:text-3xl font-bold mt-3">
              {state.loading ? 'Confirming your payment…' : state.paid ? 'Deposit received' : 'Payment pending'}
            </h1>
            <p className="text-sm opacity-90 mt-2">
              {state.loading
                ? 'Hold on a moment while we confirm with Stripe.'
                : state.paid
                  ? 'Thank you — a travel consultant will contact you within 1 working day to finalise the booking.'
                  : 'We have not yet received confirmation from Stripe. If you completed the payment, please refresh this page in a minute.'}
            </p>
          </div>

          {state.paid && (
            <div className="p-6 sm:p-8 space-y-3 text-sm" data-testid="deposit-success-summary">
              <div className="flex justify-between">
                <span className="text-slate-600">Amount paid</span>
                <span className="font-bold text-emerald-700">£{(tx.amount || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Package selected</span>
                <span className="font-medium capitalize">{tx.tier} tier</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Passengers</span>
                <span className="font-medium">{tx.passenger_count}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Package total</span>
                <span className="font-medium">£{(tx.quote_total || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Balance due before travel</span>
                <span className="font-medium">£{Math.max((tx.quote_total || 0) - (tx.amount || 0), 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-slate-200">
                <span className="text-slate-600">Quotation ID</span>
                <code className="text-xs">{tx.quotation_id?.slice(0, 8)}</code>
              </div>
            </div>
          )}

          {state.error && (
            <div className="p-6 text-sm text-red-700 bg-red-50 border-t border-red-200">
              {state.error}
            </div>
          )}

          <div className="px-6 sm:px-8 pb-6 sm:pb-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/"
              data-testid="success-back-home"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl px-4 py-3"
            >
              <Home className="h-4 w-4" /> Back to home
            </Link>
            <a
              href="tel:01908220000"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl px-4 py-3"
            >
              <Phone className="h-4 w-4" /> Call us · 01908 220000 / 01582 808050
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
